<script setup>
// Aceleres: fluxo único de "iniciar nova conversa" a partir de um telefone.
//
// Nasceu inline no ChatList.vue (Chat de Atendimento) e foi extraído para que a
// página de Contatos use exatamente o mesmo modal de seleção de instância, as
// mesmas regras de disponibilidade, o mesmo aviso de contato já em atendimento
// por outro vendedor e a mesma criação de conversa. Quem hospeda o componente
// só informa o telefone; todo o resto acontece aqui.
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import {
  buildQuickStartChannels,
  eligibleInboxesForPhone,
  isEligibleQuickStartInbox,
  isOwnedPersonalQuickStartInbox,
} from 'dashboard/helper/aceleresQuickStartChannels';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['started', 'close']);

const router = useRouter();

const currentUser = useMapGetter('getCurrentUser');
const inboxesList = useMapGetter('inboxes/getInboxes');

const currentUserId = computed(() => Number(currentUser.value?.id || 0));

const phone = ref('');
const quickInboxes = ref([]);
const quickEligibleInboxIds = ref([]);
const quickInboxPickerOpen = ref(false);
const quickCreating = ref(false);
const ownershipCheck = ref(null);
const ownershipMessageIntent = ref('');
const ownershipChecking = ref(false);
const ownershipRequesting = ref(false);

const quickStartChannels = computed(() =>
  buildQuickStartChannels({
    inboxes: inboxesList.value,
    extraInboxes: quickInboxes.value,
    eligibleInboxIds: quickEligibleInboxIds.value,
    currentUserId: currentUserId.value,
  })
);

const isBusy = computed(() => quickCreating.value || ownershipChecking.value);

function reset() {
  phone.value = '';
  quickInboxes.value = [];
  quickEligibleInboxIds.value = [];
  quickInboxPickerOpen.value = false;
  ownershipCheck.value = null;
  ownershipMessageIntent.value = '';
}

function closePicker() {
  quickInboxPickerOpen.value = false;
  emit('close');
}

// Ponto de entrada do fluxo: recebe o telefone e abre o seletor de instância.
function startForPhone(rawPhone) {
  const value = (rawPhone || '').trim();
  if (!value) return;

  phone.value = value;
  ownershipCheck.value = null;
  quickInboxPickerOpen.value = false;

  const eligibles = eligibleInboxesForPhone(
    inboxesList.value,
    currentUserId.value
  );
  quickInboxes.value = eligibles;
  quickEligibleInboxIds.value = eligibles
    .filter(inbox => isEligibleQuickStartInbox(inbox, currentUserId.value))
    .map(inbox => Number(inbox.id));

  const enabledChannels = quickStartChannels.value.filter(
    channel => channel.canStart
  );
  if (enabledChannels.length === 0) {
    if (quickStartChannels.value.length > 0) {
      quickInboxPickerOpen.value = true;
      return;
    }
    alert('Nenhum canal de WhatsApp conectado para iniciar a conversa.');
    return;
  }
  quickInboxPickerOpen.value = true;
}

async function startPhoneConversation(inboxId) {
  if (!inboxId) return;
  const accId = props.accountId;
  const src = phone.value;
  ownershipChecking.value = true;
  try {
    const check = await window.axios.post(
      `/api/v1/accounts/${accId}/contact_ownership/check`,
      {
        phone: src,
        inbox_id: inboxId,
      }
    );
    ownershipCheck.value = check.data;
    if (check.data && check.data.belongs_to_other) return;
    const existingConversationInboxId = Number(
      check.data?.existing_conversation_inbox_id || 0
    );
    if (
      check.data &&
      check.data.existing_conversation_id &&
      existingConversationInboxId === Number(inboxId)
    ) {
      quickInboxPickerOpen.value = false;
      emit('started');
      reset();
      router.push(
        `/app/accounts/${accId}/conversations/${check.data.existing_conversation_id}`
      );
      return;
    }
  } catch (e) {
    console.warn('ownership check failed', e);
    if (e?.response?.status === 422) {
      ownershipCheck.value = e.response.data || null;
      alert(
        e.response.data?.message ||
          'O canal selecionado está desconectado ou indisponível.'
      );
      return;
    }
    ownershipCheck.value = null;
  } finally {
    ownershipChecking.value = false;
  }

  quickCreating.value = true;
  try {
    const digits = (src || '').replace(/\D/g, '');
    const phoneE164 = digits.startsWith('55') ? `+${digits}` : `+55${digits}`;

    // 1) cria (ou recupera) contato
    let contactId = null;
    try {
      const r = await window.axios.post(`/api/v1/accounts/${accId}/contacts`, {
        name: phoneE164,
        phone_number: phoneE164,
      });
      contactId = r.data?.payload?.contact?.id || r.data?.id;
    } catch (e) {
      const dup =
        e?.response?.data?.contact?.id ||
        e?.response?.data?.payload?.contact?.id;
      if (dup) {
        contactId = dup;
      } else {
        const search = await window.axios.get(
          `/api/v1/accounts/${accId}/contacts/search?q=${encodeURIComponent(phoneE164)}`
        );
        contactId = search.data?.payload?.[0]?.id;
      }
    }
    if (!contactId) throw new Error('Nao conseguiu criar/encontrar contato.');

    // 2) cria contact_inbox (gera source_id)
    const ci = await window.axios.post(
      `/api/v1/accounts/${accId}/contacts/${contactId}/contact_inboxes`,
      {
        inbox_id: inboxId,
        source_id: phoneE164.replace('+', ''),
      }
    );
    const sourceId = ci.data?.payload?.source_id || phoneE164.replace('+', '');

    const selectedInbox =
      (inboxesList.value || []).find(
        inbox => Number(inbox.id) === Number(inboxId)
      ) ||
      (quickInboxes.value || []).find(
        inbox => Number(inbox.id) === Number(inboxId)
      );
    const createConversationParams = {
      source_id: sourceId,
      inbox_id: inboxId,
      contact_id: contactId,
    };

    // Selecionar a própria instância já representa uma atribuição ao dono.
    // Isso mantém a regra de privacidade: o sistema só passa a tratar o
    // atendimento como acessível depois que ele está atribuído ao vendedor.
    if (
      isOwnedPersonalQuickStartInbox(selectedInbox, currentUserId.value) &&
      currentUserId.value > 0
    ) {
      createConversationParams.assignee_id = currentUserId.value;
    }

    // 3) cria conversa SEM mensagem -> cai no compositor pronto pra digitar
    const conv = await window.axios.post(
      `/api/v1/accounts/${accId}/conversations`,
      createConversationParams
    );
    const convDisplayId = conv.data?.id || conv.data?.payload?.id;
    quickInboxPickerOpen.value = false;
    emit('started');
    reset();
    router.push({
      name: 'inbox_conversation',
      params: { accountId: accId, conversation_id: convDisplayId },
    });
  } catch (e) {
    console.error(e);
    alert(
      'Erro: ' +
        (e?.response?.data?.message ||
          e?.message ||
          'falha ao iniciar conversa')
    );
  } finally {
    quickCreating.value = false;
  }
}

async function requestOwnership(messageIntent) {
  if (!ownershipCheck.value || !ownershipCheck.value.belongs_to_other) return;
  ownershipRequesting.value = true;
  try {
    await window.axios.post('/api/chatwoot/ownership/request', {
      account_id: props.accountId,
      phone: phone.value,
      message_intent: messageIntent || null,
      owner_chatwoot_user_id: ownershipCheck.value.owner_chatwoot_user_id,
      inbox_id: ownershipCheck.value.inbox_id,
      inbox_name: ownershipCheck.value.inbox_name,
      conversation_id: ownershipCheck.value.existing_conversation_id,
      contact_id: ownershipCheck.value.contact_id,
    });
    alert(
      'Solicitação enviada para ' +
        ownershipCheck.value.owner_name +
        '. Você será notificado quando aprovado.'
    );
    reset();
    emit('close');
  } catch (e) {
    alert('Erro: ' + (e?.response?.data?.error || e?.message || 'falha'));
  } finally {
    ownershipRequesting.value = false;
  }
}

async function forceOwnershipForPietroTest() {
  if (!ownershipCheck.value?.lucas_test_override_allowed) return;
  if (
    !window.confirm(
      'Assumir esta conversa para teste solicitado por Pietro? A transferência será registrada.'
    )
  )
    return;

  ownershipRequesting.value = true;
  try {
    const accId = props.accountId;
    const result = await window.axios.post(
      `/api/v1/accounts/${accId}/contact_ownership/force_transfer_for_pietro_test`,
      { conversation_id: ownershipCheck.value.existing_conversation_id }
    );
    emit('started');
    reset();
    router.push(
      `/app/accounts/${accId}/conversations/${result.data.conversation_id}`
    );
  } catch (e) {
    alert(
      'Erro ao assumir conversa: ' +
        (e?.response?.data?.error || e?.message || 'falha')
    );
  } finally {
    ownershipRequesting.value = false;
  }
}

// Cancelar o aviso devolve o usuário ao seletor de canal, que segue montado.
function dismissOwnershipCheck() {
  ownershipCheck.value = null;
  if (!quickInboxPickerOpen.value) emit('close');
}

defineExpose({ startForPhone, reset, isBusy });
</script>

<template>
  <span class="contents">
    <!-- Modal seletor de canal/instância -->
    <Teleport to="body">
      <div
        v-if="quickInboxPickerOpen"
        class="acl-quick-start-overlay"
        @click.self="closePicker"
      >
        <div
          class="acl-quick-start-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="acl-quick-start-title"
        >
          <header class="acl-quick-start-modal__header">
            <div>
              <h3
                id="acl-quick-start-title"
                class="acl-quick-start-modal__title"
              >
                Selecione um canal
              </h3>
              <p class="acl-quick-start-modal__description">
                Iniciar nova conversa com
                <strong>{{ phone }}</strong>
              </p>
            </div>
            <button
              type="button"
              class="acl-quick-start-modal__close"
              @click="closePicker"
            >
              <span class="i-lucide-x size-5" aria-hidden="true" />
            </button>
          </header>
          <div class="acl-quick-start-modal__list">
            <button
              v-for="channel in quickStartChannels"
              :key="channel.id"
              type="button"
              class="acl-quick-start-modal__channel"
              :class="{
                'acl-quick-start-modal__channel--disabled': !channel.canStart,
              }"
              :disabled="
                quickCreating || ownershipChecking || !channel.canStart
              "
              @click="startPhoneConversation(channel.id)"
            >
              <span
                class="acl-quick-start-modal__icon"
                :style="{ color: channel.icon.color }"
                aria-hidden="true"
              >
                <span :class="channel.icon.icon" />
              </span>
              <span class="acl-quick-start-modal__content">
                <span class="acl-quick-start-modal__name">
                  {{ channel.name }}
                </span>
                <span class="acl-quick-start-modal__subtitle">
                  {{ channel.subtitle }}
                </span>
              </span>
              <span
                v-if="channel.canStart"
                class="acl-quick-start-modal__action"
              >
                Escolher instância
                <span class="i-lucide-chevron-right size-3.5" />
              </span>
              <span v-else class="acl-quick-start-modal__blocked">
                {{ channel.disabledReason }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de aviso: contato já em atendimento por outro vendedor -->
    <Teleport to="body">
      <div
        v-if="ownershipCheck && ownershipCheck.belongs_to_other"
        class="acl-quick-start-overlay acl-quick-start-overlay--stacked"
        @click.self="dismissOwnershipCheck"
      >
        <div
          class="bg-n-solid-1 border border-n-strong rounded-xl w-full max-w-md p-6"
        >
          <div class="flex items-center gap-2 mb-3">
            <span class="i-lucide-shield-alert size-6 text-amber-500" />
            <h3 class="text-base font-semibold text-n-slate-12">
              Contato com outro vendedor
            </h3>
          </div>
          <p class="text-sm text-n-slate-11 mb-4">
            O contato
            <strong class="text-n-slate-12">+{{ phone }}</strong> já está sendo
            atendido por
            <strong class="text-n-slate-12">{{
              ownershipCheck.owner_name
            }}</strong
            >{{
              ownershipCheck.inbox_name
                ? ' na inbox "' + ownershipCheck.inbox_name + '"'
                : ''
            }}.
          </p>
          <p class="text-xs text-n-slate-10 mb-4">
            Para iniciar conversa, peça autorização.
            {{
              ownershipCheck.current_user_can_force
                ? 'Como gestor, você pode forçar a transferência.'
                : ''
            }}
          </p>
          <div
            v-if="ownershipCheck.existing_request"
            class="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300 mb-4"
          >
            Você já tem uma solicitação pendente para este contato. Aguarde a
            resposta.
          </div>
          <div v-else>
            <label
              class="text-xs text-n-slate-10 font-semibold uppercase block mb-1"
            >
              Mensagem que pretende enviar (opcional)
            </label>
            <textarea
              v-model="ownershipMessageIntent"
              rows="3"
              class="w-full bg-n-alpha-1 border border-n-strong rounded-lg px-3 py-2 text-sm text-n-slate-12 resize-none mb-3"
              placeholder="Quero atender este lead porque…"
            />
          </div>
          <div class="flex gap-2 justify-end">
            <button
              class="px-4 py-2 text-n-slate-11 text-sm hover:text-n-slate-12"
              @click="dismissOwnershipCheck"
            >
              Cancelar
            </button>
            <button
              v-if="ownershipCheck.lucas_test_override_allowed"
              :disabled="
                ownershipRequesting || !ownershipCheck.existing_conversation_id
              "
              class="px-4 py-2 border border-amber-500 text-amber-400 rounded-lg text-sm font-semibold hover:bg-amber-500/10 disabled:opacity-50"
              @click="forceOwnershipForPietroTest"
            >
              Assumir para teste (Pietro)
            </button>
            <button
              v-if="!ownershipCheck.existing_request"
              :disabled="ownershipRequesting"
              class="px-5 py-2 bg-n-brand text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              @click="requestOwnership(ownershipMessageIntent)"
            >
              {{ ownershipRequesting ? 'Enviando…' : 'Solicitar autorização' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<style scoped>
.acl-quick-start-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}
/* O aviso de ownership abre por cima do seletor de canal, que continua montado
   para o usuário voltar e escolher outra instância ao cancelar. */
.acl-quick-start-overlay--stacked {
  z-index: 2147483501;
}
.acl-quick-start-modal {
  display: flex;
  flex-direction: column;
  width: min(560px, 92vw);
  height: min(78vh, 470px);
  max-height: min(78vh, 470px);
  padding: 16px;
  overflow: hidden;
  color: #111827;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
}
.acl-quick-start-modal__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.acl-quick-start-modal__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  color: #111827;
}
.acl-quick-start-modal__description {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #6b7280;
}
.acl-quick-start-modal__description strong {
  color: #111827;
  font-weight: 700;
}
.acl-quick-start-modal__list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  padding-right: 6px;
  margin-top: 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c7cdd4 transparent;
}
.acl-quick-start-modal__list::-webkit-scrollbar {
  width: 7px;
}
.acl-quick-start-modal__list::-webkit-scrollbar-thumb {
  background: #c7cdd4;
  border-radius: 999px;
}
.acl-quick-start-modal__channel {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 52px;
  gap: 12px;
  padding: 9px 12px;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition:
    border-color 0.14s ease,
    box-shadow 0.14s ease,
    transform 0.14s ease;
}
.acl-quick-start-modal__channel:not(:disabled):hover {
  border-color: #c4b5fd;
  box-shadow: 0 4px 12px rgba(91, 33, 182, 0.13);
  transform: translateY(-1px);
}
.acl-quick-start-modal__channel--disabled {
  cursor: default;
}
.acl-quick-start-modal__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  font-size: 23px;
}
.acl-quick-start-modal__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.acl-quick-start-modal__name {
  overflow: hidden;
  color: #1f2937;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acl-quick-start-modal__subtitle {
  overflow: hidden;
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acl-quick-start-modal__action {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  gap: 3px;
  color: #7c3aed;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}
.acl-quick-start-modal__blocked {
  flex: 0 1 180px;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  text-align: right;
}
.acl-quick-start-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-top: 2px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  color: #6b7280;
  background: #f9fafb;
  flex-shrink: 0;
  transition:
    color 0.14s ease,
    border-color 0.14s ease,
    background-color 0.14s ease;
}
.acl-quick-start-modal__close:hover:not(:disabled) {
  color: #111827;
  border-color: #9ca3af;
  background: #f3f4f6;
}
.acl-quick-start-modal__close .i-lucide-x {
  pointer-events: none;
}
:global(.dark) .acl-quick-start-modal {
  color: #f9fafb;
  background: #111315;
  border-color: #2f343a;
}
:global(.dark) .acl-quick-start-modal__title,
:global(.dark) .acl-quick-start-modal__description strong,
:global(.dark) .acl-quick-start-modal__name,
:global(.dark) .acl-quick-start-modal__close {
  color: #f9fafb;
}
:global(.dark) .acl-quick-start-modal__description,
:global(.dark) .acl-quick-start-modal__subtitle {
  color: #a1a1aa;
}
:global(.dark) .acl-quick-start-modal__channel {
  background: #171a1d;
  border-color: #2f343a;
}
:global(.dark) .acl-quick-start-modal__blocked {
  color: #cbd5e1;
}
:global(.dark) .acl-quick-start-modal__close {
  border-color: #3f454d;
  background: #1f2328;
}
:global(.dark) .acl-quick-start-modal__close:hover:not(:disabled) {
  border-color: #565d67;
  background: #252b31;
}
.acl-quick-start-modal__action,
.acl-quick-start-modal__blocked {
  flex-shrink: 0;
}
@media (max-width: 640px) {
  .acl-quick-start-modal {
    width: min(560px, 96vw);
    height: min(88vh, 470px);
    padding: 14px 12px 12px;
  }
  .acl-quick-start-modal__channel {
    align-items: flex-start;
    gap: 10px;
  }
  .acl-quick-start-modal__action,
  .acl-quick-start-modal__blocked {
    flex-basis: 120px;
    white-space: normal;
  }
}
</style>
